import React, { useState } from 'react';
import { Package, Download, PlayCircle, Lock, ExternalLink, Star, MessageSquare, AlertCircle } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface PurchasesTabProps {
  onNavigate: (page: string) => void;
}
export function PurchasesTab({
  onNavigate
}: PurchasesTabProps) {
  const [activeFilter, setActiveFilter] = useState('Все');
  const [reviewFormOpen, setReviewFormOpen] = useState<string | null>(null);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  // Mock purchased products with progress and access status
  const purchasedProducts = [{
    id: 'PROD-001',
    title: 'Основы ведической астрологии: с нуля до первых прогнозов',
    type: 'курс',
    authorName: 'Елена Смирнова',
    authorAvatar: 'ЕС',
    coverColor: 'bg-indigo-100',
    purchaseDate: '12 Окт 2024',
    pricePaid: '15 000 ₽',
    accessStatus: 'granted',
    progress: {
      current: 7,
      total: 12,
      percent: 58
    },
    materials: [{
      type: 'video',
      name: 'Модуль 1: Введение (12 уроков)'
    }]
  }, {
    id: 'PROD-002',
    title: 'Гайд: Как пережить транзит Сатурна без потерь',
    type: 'pdf',
    authorName: 'Анна Коваль',
    authorAvatar: 'АК',
    coverColor: 'bg-slate-200',
    purchaseDate: '05 Окт 2024',
    pricePaid: '1 490 ₽',
    accessStatus: 'granted',
    progress: null,
    materials: [{
      type: 'pdf',
      name: 'Гайд_Транзиты_Сатурна.pdf'
    }]
  }, {
    id: 'PROD-003',
    title: 'Марафон: Пробуждение Венеры (Отношения и Финансы)',
    type: 'марафон',
    authorName: 'Елена Смирнова',
    authorAvatar: 'ЕС',
    coverColor: 'bg-pink-100',
    purchaseDate: '01 Окт 2024',
    pricePaid: '4 900 ₽',
    accessStatus: 'granted',
    progress: {
      current: 14,
      total: 21,
      percent: 66
    },
    materials: [{
      type: 'video',
      name: '21 видеоурок'
    }]
  }, {
    id: 'PROD-004',
    title: 'Запись вебинара: Финансовые коды в натальной карте',
    type: 'запись',
    authorName: 'Михаил Светлов',
    authorAvatar: 'МС',
    coverColor: 'bg-emerald-100',
    purchaseDate: '28 Сен 2024',
    pricePaid: '2 900 ₽',
    accessStatus: 'granted',
    progress: null,
    materials: [{
      type: 'video',
      name: 'Запись_вебинара_Финансы.mp4'
    }]
  }, {
    id: 'PROD-005',
    title: 'Чек-лист: Совместимость партнёров (Синастрия)',
    type: 'чек-лист',
    authorName: 'Анна Коваль',
    authorAvatar: 'АК',
    coverColor: 'bg-rose-100',
    purchaseDate: 'Сегодня',
    pricePaid: '490 ₽',
    accessStatus: 'pending',
    progress: null,
    materials: [{
      type: 'pdf',
      name: 'Чек-лист_Синастрия.pdf'
    }]
  }];
  const filters = ['Все', 'Курсы', 'PDF', 'Записи', 'Марафоны'];
  const filteredProducts = purchasedProducts.filter(p => {
    if (activeFilter === 'Все') return true;
    if (activeFilter === 'Курсы') return p.type === 'курс';
    if (activeFilter === 'PDF') return p.type === 'pdf' || p.type === 'чек-лист';
    if (activeFilter === 'Записи') return p.type === 'запись';
    if (activeFilter === 'Марафоны') return p.type === 'марафон';
    return true;
  });
  const handleSubmitReview = (id: string) => {
    // In a real app, this would call an API
    alert(`Отзыв отправлен! Оценка: ${rating}, Текст: ${reviewText}`);
    setReviewFormOpen(null);
    setRating(0);
    setReviewText('');
  };
  const renderActionButtons = (product: (typeof purchasedProducts)[0]) => {
    if (product.accessStatus === 'pending') {
      return <Button className="w-full" variant="outline" disabled icon={<Lock className="w-4 h-4" data-id="element-16071" />} data-id="element-16070">
          Ожидание доступа
        </Button>;
    }
    switch (product.type) {
      case 'курс':
        return <Button className="w-full" variant="primary" icon={<PlayCircle className="w-4 h-4" data-id="element-16073" />} data-id="element-16072">
            Продолжить обучение
          </Button>;
      case 'марафон':
        return <Button className="w-full" variant="primary" icon={<PlayCircle className="w-4 h-4" data-id="element-16075" />} data-id="element-16074">
            Перейти к марафону
          </Button>;
      case 'pdf':
      case 'чек-лист':
        return <div className="flex gap-2" data-id="element-16076">
            <Button className="flex-1" variant="primary" icon={<Download className="w-4 h-4" data-id="element-16078" />} data-id="element-16077">
              Скачать
            </Button>
            <Button className="flex-1" variant="outline" icon={<ExternalLink className="w-4 h-4" data-id="element-16080" />} data-id="element-16079">
              Открыть
            </Button>
          </div>;
      case 'запись':
        return <Button className="w-full" variant="primary" icon={<PlayCircle className="w-4 h-4" data-id="element-16082" />} data-id="element-16081">
            Смотреть запись
          </Button>;
      default:
        return <Button className="w-full" variant="primary" data-id="element-16083">
            Открыть материалы
          </Button>;
    }
  };
  return <div className="space-y-6" data-id="element-16084">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-16085">
        <div data-id="element-16086">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-16087">Мои покупки</h2>
          <p className="text-sm text-slate-500 mt-1" data-id="element-16088">
            Купленные курсы, вебинары и материалы ({purchasedProducts.length})
          </p>
        </div>
        <Button variant="outline" onClick={() => onNavigate('marketplace')} icon={<Package className="w-4 h-4" data-id="element-16090" />} data-id="element-16089">
          В каталог
        </Button>
      </div>

      {purchasedProducts.length === 0 ? <Card padding="xl" className="text-center" data-id="element-16091">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4" data-id="element-16092">
            <Package className="w-8 h-8 text-slate-400" data-id="element-16093" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-16094">
            У вас пока нет покупок
          </h3>
          <p className="text-slate-500 mb-6 max-w-md mx-auto" data-id="element-16095">
            В маркетплейсе вы найдете обучающие курсы, гайды и вебинары от
            лучших астрологов платформы.
          </p>
          <Button onClick={() => onNavigate('marketplace')} data-id="element-16096">
            Перейти в маркетплейс
          </Button>
        </Card> : <>
          <div className="flex gap-2 border-b border-slate-200 pb-px overflow-x-auto scrollbar-hide" data-id="element-16097">
            {filters.map(filter => <button key={filter} onClick={() => setActiveFilter(filter)} className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${activeFilter === filter ? 'bg-white border border-slate-200 border-b-white text-indigo-600 -mb-px relative z-10' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50 border border-transparent'}`} data-id="element-16098">
                {filter}
              </button>)}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4" data-id="element-16099">
            {filteredProducts.map(product => <Card key={product.id} padding="none" className="overflow-hidden flex flex-col border-slate-200" data-id="element-16100">
                <div className={`h-28 ${product.coverColor} relative flex items-center justify-center`} data-id="element-16101">
                  <Package className="w-12 h-12 text-slate-700 opacity-20" data-id="element-16102" />
                  <div className="absolute top-3 left-3 flex gap-2" data-id="element-16103">
                    <Badge variant="neutral" className="bg-white/90 backdrop-blur-sm text-slate-800 border-none shadow-sm capitalize" data-id="element-16104">
                      {product.type}
                    </Badge>
                    <Badge variant={product.accessStatus === 'granted' ? 'success' : 'warning'} className="shadow-sm" data-id="element-16105">
                      {product.accessStatus === 'granted' ? 'Доступ открыт' : 'Ожидание доступа'}
                    </Badge>
                  </div>
                </div>

                <div className="p-5 flex-1 flex flex-col" data-id="element-16106">
                  <h3 className="font-bold text-lg text-slate-900 mb-3 line-clamp-2" title={product.title} data-id="element-16107">
                    {product.title}
                  </h3>

                  <div className="flex items-center justify-between mb-4" data-id="element-16108">
                    <div className="flex items-center gap-2" data-id="element-16109">
                      <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-[10px] font-bold" data-id="element-16110">
                        {product.authorAvatar}
                      </div>
                      <span className="text-sm text-slate-600" data-id="element-16111">
                        {product.authorName}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 text-right" data-id="element-16112">
                      <div data-id="element-16113">Куплено: {product.purchaseDate}</div>
                      <div className="font-medium text-slate-600" data-id="element-16114">
                        {product.pricePaid}
                      </div>
                    </div>
                  </div>

                  {product.progress && <div className="mb-5" data-id="element-16115">
                      <div className="flex justify-between text-xs font-medium text-slate-700 mb-1.5" data-id="element-16116">
                        <span data-id="element-16117">
                          Прогресс: {product.progress.current} из{' '}
                          {product.progress.total}
                        </span>
                        <span data-id="element-16118">{product.progress.percent}%</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-16119">
                        <div className="bg-emerald-500 h-2 rounded-full" style={{
                  width: `${product.progress.percent}%`
                }} data-id="element-16120"></div>
                      </div>
                    </div>}

                  <div className="mt-auto space-y-4" data-id="element-16121">
                    {renderActionButtons(product)}

                    <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-16122">
                      <button onClick={() => setReviewFormOpen(reviewFormOpen === product.id ? null : product.id)} className="text-xs font-medium text-indigo-600 hover:text-indigo-700 flex items-center gap-1 transition-colors" data-id="element-16123">
                        <Star className="w-3.5 h-3.5" data-id="element-16124" />
                        Оставить отзыв
                      </button>
                      <button className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 transition-colors" data-id="element-16125">
                        <AlertCircle className="w-3.5 h-3.5" data-id="element-16126" />
                        Проблема с доступом?
                      </button>
                    </div>

                    {/* Inline Review Form */}
                    {reviewFormOpen === product.id && <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mt-3 animate-[fadeIn_0.2s_ease-out]" data-id="element-16127">
                        <div className="flex justify-between items-center mb-3" data-id="element-16128">
                          <h4 className="text-sm font-bold text-slate-900" data-id="element-16129">
                            Ваш отзыв
                          </h4>
                          <div className="flex gap-1" data-id="element-16130">
                            {[1, 2, 3, 4, 5].map(star => <button key={star} onClick={() => setRating(star)} className="focus:outline-none" data-id="element-16131">
                                <Star className={`w-5 h-5 ${rating >= star ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`} data-id="element-16132" />
                              </button>)}
                          </div>
                        </div>
                        <textarea className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none mb-3" rows={3} placeholder="Поделитесь впечатлениями о материале..." value={reviewText} onChange={e => setReviewText(e.target.value)} data-id="element-16133" />
                        <div className="flex justify-end gap-2" data-id="element-16134">
                          <Button variant="ghost" size="sm" onClick={() => setReviewFormOpen(null)} data-id="element-16135">
                            Отмена
                          </Button>
                          <Button variant="primary" size="sm" onClick={() => handleSubmitReview(product.id)} disabled={rating === 0 || reviewText.trim() === ''} data-id="element-16136">
                            Отправить
                          </Button>
                        </div>
                      </div>}
                  </div>
                </div>
              </Card>)}
          </div>

          {filteredProducts.length === 0 && <div className="text-center py-12 bg-white rounded-xl border border-slate-200" data-id="element-16137">
              <p className="text-slate-500" data-id="element-16138">
                В этой категории пока нет покупок
              </p>
            </div>}
        </>}
    </div>;
}